import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DEFAULT_TARGET, RICHMENU_DEFAULT_DIVIND } from "@constants";
import { EMessageActionType, IRichmenuBoundAction } from "@types";
import { useRichmenuStore } from "@store/richmenuStore";
import { shallow } from "zustand/shallow";
import { useAppStore } from "@store/appStore";
import { useRichmenu } from "./useRichmenu";

export const RichMenu = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { richmenu, isOpenRichmenu } = useRichmenuStore((state) => {
    return {
      richmenu: state.richmenu,
      isOpenRichmenu: state.isOpenRichmenu,
    };
  }, shallow);
  const { richmenuSetting, linkTarget = DEFAULT_TARGET } = useAppStore(
    (state) => {
      return {
        richmenuSetting: state.settings.richmenuSetting,
        linkTarget: state.settings.linkTarget,
      };
    },
    shallow,
  );
  const { onSendRichmenuAction } = useRichmenu();

  useEffect(() => {
    const containerElement = containerRef.current;

    if (!containerElement) return;

    const updateContainerWidth = () => {
      setContainerWidth(containerElement.getBoundingClientRect().width);
    };

    updateContainerWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateContainerWidth();
    });

    resizeObserver.observe(containerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const _renderActions = useCallback((actions: IRichmenuBoundAction[]) => {
    return actions.map(({ action, bounds }) => {
      if (action.type === EMessageActionType.URI_ACTION) {
        return (
          <a
            key={action.id}
            className="absolute"
            href={action.uri}
            target={linkTarget}
            style={{
              top: `${bounds.y}%`,
              left: `${bounds.x}%`,
              width: `${bounds.width}%`,
              height: `${bounds.height}%`,
            }}
          />
        );
      }

      if (action.type === EMessageActionType.CALL_ACTION) {
        return (
          <a
            key={action.id}
            className="absolute"
            href={`tel:${action.phoneNo}`}
            style={{
              top: `${bounds.y}%`,
              left: `${bounds.x}%`,
              width: `${bounds.width}%`,
              height: `${bounds.height}%`,
            }}
          />
        );
      }

      if (
        action.type === EMessageActionType.MESSAGE_ACTION ||
        action.type === EMessageActionType.TEMPLATE_ACTION ||
        action.type === EMessageActionType.FLOW_ACTION
      ) {
        return (
          <button
            key={action.id}
            onClick={() => {
              onSendRichmenuAction(action);
            }}
            className="absolute"
            style={{
              top: `${bounds.y}%`,
              left: `${bounds.x}%`,
              width: `${bounds.width}%`,
              height: `${bounds.height}%`,
            }}
          />
        );
      }
      return null;
    });
  }, []);

  const richmenuAspectRatio = useMemo(() => {
    if (richmenu?.size?.width && richmenu?.size?.height) {
      return richmenu.size.width / richmenu.size.height;
    }

    return RICHMENU_DEFAULT_DIVIND;
  }, [richmenu?.size?.height, richmenu?.size?.width]);

  const richmenuWidth = useMemo(() => {
    if (!containerWidth) {
      return 0;
    }

    if (richmenuSetting?.fixedHeight) {
      return Math.min(
        containerWidth,
        richmenuSetting.fixedHeight * richmenuAspectRatio,
      );
    }

    if (richmenuSetting?.dividedBy) {
      return containerWidth / richmenuSetting.dividedBy;
    }

    return containerWidth;
  }, [
    containerWidth,
    richmenuAspectRatio,
    richmenuSetting?.dividedBy,
    richmenuSetting?.fixedHeight,
  ]);

  const richmenuHeight = useMemo(() => {
    if (!richmenuWidth) {
      return 0;
    }

    return richmenuWidth / richmenuAspectRatio;
  }, [richmenuAspectRatio, richmenuWidth]);

  return (
    <div
      ref={containerRef}
      style={{
        height: !isOpenRichmenu ? 0 : richmenuHeight,
      }}
      className={`relative w-full overflow-hidden transition-all duration-200 ease-in-out`}
    >
      <div className="relative max-w-full">
        {richmenu ? _renderActions(richmenu.areas) : null}
        <img className="block h-auto w-full" src={richmenu?.image ?? ""} />
      </div>
    </div>
  );
};
